/**
 * Cova Feed — branded IDs + closed enums.
 *
 * `FeedPostStatus` is a genuinely closed lifecycle (draft → published →
 * archived), so a Schema.Literal is correct. The user-COMPOSABLE taxonomy
 * (post categories) is a row in feed_categories, not an enum here — per
 * user-empowerment.md.
 */
import { Schema as S } from "effect"

// ============================================================================
// Branded IDs
// ============================================================================

export const FeedPostId = S.UUID.pipe(
  S.brand("FeedPostId"),
  S.annotations({
    identifier: "FeedPostId",
    title: "Feed Post ID",
    description: "UUID identifier for a feed post (or threaded reply)",
  }),
)
export type FeedPostId = typeof FeedPostId.Type

export const FeedCategoryId = S.UUID.pipe(
  S.brand("FeedCategoryId"),
  S.annotations({
    identifier: "FeedCategoryId",
    title: "Feed Category ID",
    description: "UUID identifier for a feed category",
  }),
)
export type FeedCategoryId = typeof FeedCategoryId.Type

export const FeedCommentId = S.UUID.pipe(
  S.brand("FeedCommentId"),
  S.annotations({
    identifier: "FeedCommentId",
    title: "Feed Comment ID",
    description: "UUID identifier for a Cova-team-only feed comment",
  }),
)
export type FeedCommentId = typeof FeedCommentId.Type

// ============================================================================
// Post lifecycle
// ============================================================================

/**
 * draft     — authored, not yet visible to customers
 * published — live in the feed (published_at set)
 * archived  — withdrawn from the feed but kept
 */
export const FeedPostStatus = S.Literal("draft", "published", "archived")
export type FeedPostStatus = typeof FeedPostStatus.Type

/**
 * How a post's body is serialised.
 *
 * `markdown` — pre-v4.88 posts. The card renders `body_markdown` through
 *              `FeedMarkdown`.
 * `pm`       — ProseMirror-era. `body_doc` is the record of truth and the card
 *              renders the sanitised `body_html` projection.
 *
 * A STORAGE DISCRIMINATOR, NOT A USER-FACING CONCEPT — the author never sees,
 * names or chooses it, so a closed literal is correct here and
 * user-empowerment.md's "ship a primitive" rule does not apply (the same
 * reasoning `client_emails.body_format` records).
 *
 * The `markdown` branch is PERMANENT, not transitional. A stale client can
 * still submit markdown after the rollout, so "every row is 'pm'" is never a
 * durable invariant and no reader may assume it.
 */
export const FeedBodyFormat = S.Literal("markdown", "pm")
export type FeedBodyFormat = typeof FeedBodyFormat.Type

/**
 * What a `feed_post_views` row records.
 *
 * `impression` — the post was put in front of the reader: its card was at least
 * half-visible in the viewport for ~1s on /feed, or they dismissed or acted on
 * its spotlight popup (written with the spotlight's seen row, once per viewer
 * per spotlight version). `article_open` — the reader opened the long-form body.
 *
 * Genuinely closed by domain reality, not by our imagination: each value is a
 * distinct instrumented MOMENT in the client, so adding one means writing the
 * code that detects it. A `feed_view_kinds` table could not detect anything.
 */
export const FeedViewKind = S.Literal("impression", "article_open")
export type FeedViewKind = typeof FeedViewKind.Type
